import { Navigate } from 'react-router-dom';

import { permissions } from '@/modules/auth/permissions';
import { UserAdministration } from '@/pages/administration/UserAdministration';
import Companies from '@/pages/companies/Companies';
import CompanyAdd from '@/pages/companies/CompanyAdd';
import CompanyEdit from '@/pages/companies/CompanyEdit';
import CoordinationProcessDetail from '@/pages/coordination/CoordinationProcessDetail';
import { JobSourcingDebug } from '@/pages/debug/JobSourcingDebug';
import { LinkedInDebug } from '@/pages/debug/LinkedInDebug';
import { RecruiterSlackConnectResult } from '@/pages/integrations/RecruiterSlackConnectResult';
import DuplicateJob from '@/pages/jobs/DuplicateJob';
import JobAdd from '@/pages/jobs/JobAdd';
import JobCandidate from '@/pages/jobs/JobCandidate';
import JobCandidates from '@/pages/jobs/JobCandidates';
import { JobEdit } from '@/pages/jobs/JobEdit';
import Jobs from '@/pages/jobs/Jobs';
import Messaging from '@/pages/messaging/Messaging';
import UserAdd from '@/pages/users/UserAdd';
import UserEdit from '@/pages/users/UserEdit';
import UserExternalAccounts from '@/pages/users/UserExternalAccounts';
import Users from '@/pages/users/Users';
import { isProdEnv } from '@/utils/env';

import type { PageConfig } from './types';

export const pages: PageConfig[] = [
  {
    path: '/',
    component: <Jobs />,
    breadcrumbs: [{ title: 'Jobs', path: '/' }],
    permissions: [permissions.Jobs.Read],
    metadata: {
      title: 'Jobs',
    },
  },
  {
    path: '/companies',
    component: <Companies />,
    breadcrumbs: [{ title: 'Companies', path: '/companies' }],
    permissions: [permissions.Companies.Read],
    metadata: {
      title: 'Companies',
    },
  },
  {
    path: '/companies/add',
    component: <CompanyAdd />,
    breadcrumbs: [
      { title: 'Companies', path: '/companies' },
      { title: 'Add', path: '/companies/add' },
    ],
    permissions: [permissions.Companies.Create],
    metadata: {
      title: 'Add Company',
    },
  },
  {
    path: '/companies/:id/edit',
    component: <CompanyEdit />,
    breadcrumbs: [
      { title: 'Companies', path: '/companies' },
      { title: 'Edit', path: '/companies/:id/edit' },
    ],
    permissions: [permissions.Companies.Read],
    metadata: {
      title: 'Edit Company',
    },
  },
  {
    path: '/jobs',
    component: <Jobs />,
    breadcrumbs: [{ title: 'Jobs', path: '/jobs' }],
    permissions: [permissions.Jobs.Read],
    metadata: {
      title: 'Jobs',
    },
  },
  {
    path: '/jobs/add',
    component: <JobAdd />,
    breadcrumbs: [
      { title: 'Jobs', path: '/jobs' },
      { title: 'Add', path: '/jobs/add' },
    ],
    permissions: [permissions.Jobs.Create],
    metadata: {
      title: 'Add Job',
    },
  },
  {
    path: '/jobs/:id/edit',
    component: <JobEdit />,
    breadcrumbs: [
      { title: 'Jobs', path: '/jobs' },
      { title: 'Edit', path: '/jobs/:id/edit' },
    ],
    permissions: [permissions.Jobs.Read],
    metadata: {
      title: 'Edit Job',
    },
  },
  {
    path: '/jobs/:id/duplicate',
    component: <DuplicateJob />,
    breadcrumbs: [
      { title: 'Jobs', path: '/jobs' },
      { title: 'Duplicate', path: '/jobs/:id/duplicate' },
    ],
    permissions: [permissions.Jobs.Create],
    metadata: {
      title: 'Duplicate Job',
    },
  },
  {
    path: '/jobs/:id/candidates',
    component: <JobCandidates />,
    breadcrumbs: [
      { title: 'Jobs', path: '/jobs' },
      { title: 'Candidates', path: '/jobs/:id/candidates' },
    ],
    permissions: [permissions.CandidateJobs.Read],
    metadata: {
      title: 'Job Candidates',
    },
  },
  {
    path: '/jobs/:jobId/candidates/:candidateId',
    component: <JobCandidate />,
    breadcrumbs: [
      { title: 'Jobs', path: '/jobs' },
      { title: 'Candidates', path: '/jobs/:jobId/candidates' },
      { title: 'Candidate', path: '/jobs/:jobId/candidates/:candidateId' },
    ],
    permissions: [permissions.CandidateJobs.Read],
    metadata: {
      title: 'Job Candidate',
    },
  },
  {
    path: '/jobs/:jobId/coordination/:coordinationProcessId',
    component: <CoordinationProcessDetail />,
    breadcrumbs: [
      { title: 'Jobs', path: '/jobs' },
      { title: 'Candidates', path: '/jobs/:jobId/candidates' },
      {
        title: 'Coordination',
        path: '/jobs/:jobId/coordination/:coordinationProcessId',
      },
    ],
    permissions: [permissions.CandidateJobs.Read],
    metadata: {
      title: 'Coordination',
    },
  },
  {
    path: '/users',
    component: <Users />,
    breadcrumbs: [{ title: 'Users', path: '/users' }],
    permissions: [permissions.Users.Read],
    metadata: {
      title: 'Users',
    },
  },
  {
    path: '/users/add',
    component: <UserAdd />,
    breadcrumbs: [
      { title: 'Users', path: '/users' },
      { title: 'Add', path: '/users/add' },
    ],
    permissions: [permissions.Users.Create],
    metadata: {
      title: 'Add User',
    },
  },
  {
    path: '/users/:id/edit',
    component: <UserEdit />,
    breadcrumbs: [
      { title: 'Users', path: '/users' },
      { title: 'Edit', path: '/users/:id/edit' },
    ],
    permissions: [permissions.Users.Read],
    metadata: {
      title: 'Edit User',
    },
  },
  {
    path: '/users/:id/external-accounts',
    component: <UserExternalAccounts />,
    breadcrumbs: [
      { title: 'Users', path: '/users' },
      { title: 'External Accounts', path: '/users/:id/external-accounts' },
    ],
    permissions: [permissions.UserExternalAccounts.Read],
    metadata: {
      title: 'User External Accounts',
    },
  },
  {
    // Legacy dashboard path: the dashboard was removed and jobs is the recruiter home.
    path: '/dashboard',
    component: <Navigate to="/jobs" replace />,
    permissions: [],
  },
  {
    path: '/messaging',
    component: <Messaging />,
    breadcrumbs: [{ title: 'Messaging', path: '/messaging' }],
    permissions: [permissions.Messaging.Read],
    metadata: {
      title: 'Messaging',
    },
  },
  {
    path: '/slack-connect/:status',
    component: <RecruiterSlackConnectResult />,
    breadcrumbs: [{ title: 'Slack connect', path: '/slack-connect/:status' }],
    permissions: [],
    metadata: {
      title: 'Slack connect',
    },
  },
  {
    path: '/administration/users',
    component: <UserAdministration />,
    breadcrumbs: [
      { title: 'User Administration', path: '/administration/users' },
    ],
    permissions: [permissions.Administration.All],
    metadata: {
      title: 'User Administration',
    },
  },
  {
    path: '/debug/job-sourcing/:jobId',
    component: <JobSourcingDebug />,
    breadcrumbs: [
      { title: 'Job Sourcing', path: '/debug/job-sourcing/:jobId' },
    ],
    permissions: [permissions.Administration.All],
    metadata: {
      title: 'Job Sourcing Debug',
    },
  },
  ...(!isProdEnv
    ? [
        {
          path: '/debug/linkedin',
          component: <LinkedInDebug />,
          breadcrumbs: [
            { title: 'Messaging simulator', path: '/debug/linkedin' },
          ],
          permissions: [permissions.Administration.All],
          metadata: {
            title: 'Messaging simulator',
          },
        } satisfies PageConfig,
      ]
    : []),
];

export const routes = pages.map((page) => ({
  path: page.path,
}));
